// packages/core/mesh/teleport-extension-gossip/src/gossip.ts
import { scheduleTask, Event } from "@dxos/async";
import { Context } from "@dxos/context";
import { PublicKey } from "@dxos/keys";
import { log as log2 } from "@dxos/log";
import { ComplexMap, ComplexSet } from "@dxos/util";

// packages/core/mesh/teleport-extension-gossip/src/gossip-extension.ts
import assert from "@dxos/node-std/assert";
import { Trigger } from "@dxos/async";
import { log } from "@dxos/log";
import { schema } from "@dxos/protocols";
import { createProtoRpcPeer } from "@dxos/rpc";
var GossipExtension = class {
  constructor(_callbacks = {}) {
    this._callbacks = _callbacks;
    this._opened = new Trigger();
    this._closed = false;
  }
  async onOpen(context) {
    log("onOpen", {
      localPeerId: context.localPeerId,
      remotePeerId: context.remotePeerId
    }, {
      file: "gossip-extension.ts",
      line: 40,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._rpc = createProtoRpcPeer({
      requested: {
        GossipService: schema.getService("dxos.mesh.teleport.gossip.GossipService")
      },
      exposed: {
        GossipService: schema.getService("dxos.mesh.teleport.gossip.GossipService")
      },
      handlers: {
        GossipService: {
          announce: async (message) => {
            var _a, _b;
            log("received announce", {
              localPeerId: context.localPeerId,
              remotePeerId: context.remotePeerId,
              message
            }, {
              file: "gossip-extension.ts",
              line: 52,
              scope: this,
              callSite: (f, a) => f(...a)
            });
            await ((_b = (_a = this._callbacks).onAnnounce) == null ? void 0 : _b.call(_a, message));
          }
        }
      },
      port: context.createPort("rpc", {
        contentType: 'application/x-protobuf; messageType="dxos.rpc.Message"'
      })
    });
    await this._rpc.open();
    this._opened.wake();
  }
  async onClose(err) {
    var _a, _b, _c;
    log("close", {
      err
    }, {
      file: "gossip-extension.ts",
      line: 64,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await ((_a = this._rpc) == null ? void 0 : _a.close());
    this._sendInterval && clearInterval(this._sendInterval);
    await ((_c = (_b = this._callbacks).onClose) == null ? void 0 : _c.call(_b, err));
    this._closed = true;
  }
  async sendAnnounce(message) {
    if (this._closed) {
      return;
    }
    await this._opened.wait();
    assert(this._rpc, "RPC not initialized");
    await this._rpc.rpc.GossipService.announce(message);
  }
};

// packages/core/mesh/teleport-extension-gossip/src/gossip.ts
var Gossip = class {
  constructor(_params) {
    this._params = _params;
    this._ctx = new Context({
      onError: (err) => {
        log2.catch(err, {}, {
          file: "gossip.ts",
          line: 27,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
    });
    this._listeners = /* @__PURE__ */ new Map();
    this._receivedMessages = new ComplexSet(PublicKey.hash);
    this._connections = new ComplexMap(PublicKey.hash);
    this.connectionClosed = new Event();
  }
  getConnections() {
    return Array.from(this._connections.keys());
  }
  createExtension({ remotePeerId }) {
    const extension = new GossipExtension({
      onAnnounce: async (message) => {
        if (this._receivedMessages.has(message.messageId)) {
          return;
        }
        this._receivedMessages.add(message.messageId);
        this._callListeners(message);
        scheduleTask(this._ctx, async () => {
          await this._propagateAnnounce(message);
        });
      },
      onClose: async (err) => {
        if (err) {
          log2.catch(err, {}, {
            file: "gossip.ts",
            line: 60,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
        if (this._connections.has(remotePeerId)) {
          this._connections.delete(remotePeerId);
        }
        this.connectionClosed.emit(remotePeerId);
      }
    });
    this._connections.set(remotePeerId, extension);
    return extension;
  }
  async destroy() {
    await this._ctx.dispose();
  }
  postMessage(channel, payload) {
    return Promise.all([
      ...this._connections.values()
    ].map((extension) => extension.sendAnnounce({
      peerId: this._params.localPeerId,
      messageId: PublicKey.random(),
      channelId: channel,
      timestamp: new Date(),
      payload
    }).catch((err) => log2.warn(err, {}, {
      file: "gossip.ts",
      line: 88,
      scope: this,
      callSite: (f, a) => f(...a)
    }))));
  }
  listen(channel, callback) {
    if (!this._listeners.has(channel)) {
      this._listeners.set(channel, /* @__PURE__ */ new Set());
    }
    this._listeners.get(channel).add(callback);
    return {
      unsubscribe: () => {
        this._listeners.get(channel).delete(callback);
      }
    };
  }
  _callListeners(message) {
    if (this._listeners.has(message.channelId)) {
      this._listeners.get(message.channelId).forEach((callback) => {
        callback(message);
      });
    }
  }
  _propagateAnnounce(message) {
    return Promise.all([
      ...this._connections.entries()
    ].map(async ([remotePeerId, extension]) => {
      if (this._params.localPeerId.equals(message.peerId) || remotePeerId.equals(message.peerId)) {
        return;
      }
      return extension.sendAnnounce(message).catch((err) => log2.warn(err, {}, {
        file: "gossip.ts",
        line: 120,
        scope: this,
        callSite: (f, a) => f(...a)
      }));
    }));
  }
};

// packages/core/mesh/teleport-extension-gossip/src/presence.ts
import assert2 from "@dxos/node-std/assert";
import { Event as Event2, scheduleTaskInterval } from "@dxos/async";
import { Context as Context2 } from "@dxos/context";
import { PublicKey as PublicKey2 } from "@dxos/keys";
import { log as log3 } from "@dxos/log";
import { ComplexMap as ComplexMap2 } from "@dxos/util";
var PRESENCE_CHANNEL_ID = "dxos.mesh.presence.Presence";
var Presence = class {
  // remotePeerId -> PresenceExtension
  constructor(_params) {
    this._params = _params;
    this.updated = new Event2();
    this._ctx = new Context2({
      onError: (err) => {
        log3.catch(err, {}, {
          file: "presence.ts",
          line: 49,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
    });
    this._peerStates = new ComplexMap2(PublicKey2.hash);
    assert2(this._params.announceInterval < this._params.offlineTimeout, "Announce interval should be less than offline timeout.");
    this._params.gossip.listen(PRESENCE_CHANNEL_ID, (message) => {
      this._receiveAnnounces(message);
    });
    scheduleTaskInterval(this._ctx, async () => {
      const peerState = {
        "@type": "dxos.mesh.presence.PeerState",
        identityKey: this._params.identityKey,
        connections: this._params.gossip.getConnections()
      };
      await this._params.gossip.postMessage(PRESENCE_CHANNEL_ID, peerState);
    }, _params.announceInterval);
    scheduleTaskInterval(this._ctx, async () => {
      this.updated.emit();
    }, _params.offlineTimeout);
    this._params.gossip.connectionClosed.on((peerId) => {
      this._peerStates.delete(peerId);
      this.updated.emit();
    });
  }
  getPeers() {
    return Array.from(this._peerStates.values()).map((message) => message.payload);
  }
  getPeersOnline() {
    return Array.from(this._peerStates.values()).filter((message) => message.timestamp.getTime() > Date.now() - this._params.offlineTimeout).map((message) => message.payload);
  }
  async destroy() {
    await this._ctx.dispose();
  }
  _receiveAnnounces(message) {
    assert2(message.channelId === PRESENCE_CHANNEL_ID, `Invalid channel ID: ${message.channelId}`);
    const oldPeerState = this._peerStates.get(message.peerId);
    if (!oldPeerState || oldPeerState.timestamp.getTime() < message.timestamp.getTime()) {
      this._peerStates.set(message.peerId, message);
      this.updated.emit();
    }
  }
};
export {
  Gossip,
  GossipExtension,
  Presence
};
//# sourceMappingURL=index.mjs.map
